/* eslint-disable max-len */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReferenceType } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferencesStockRequest';
import { RootState } from 'app/AppStore';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { SFlex } from 'components/ReferencePriceSection/ReferencePriceSection.styled';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import { LIFECYCLE_W1, LIFECYCLE_W2, LIFECYCLE_W3, LIFECYCLE_W4 } from 'domains/catalog/Catalog.types';
import { useFetchLaborTimeIdsByReferenceGenericPart } from 'domains/laborTime/LaborTime.requests';
import { getLaborTimeIdsForGenericPart } from 'domains/laborTime/LaborTime.store';
import {
  DHReferenceLocal,
  getIsMprAccepted,
  getIsStockAvailable,
  getStockInfo,
  setSelectedTab,
} from 'domains/references';
import { useFetchMprValidation, useFetchTireReplacementProducts } from 'domains/tires/Tire.requests';
import { getTireSearchResult } from 'domains/tires/Tire.store';
import { getUserCountry, SparePartsViewType } from 'domains/user';
import Applicability from 'pages/CatalogPage/DH/Product/Applicability';
import { ProductMenu } from 'pages/CatalogPage/DH/Product/ProductMenu/ProductMenu';
import { GrayedOut } from 'pages/CatalogPage/DH/Product/ProductTab.styled';
import { ProductTopSection } from 'pages/CatalogPage/DH/Product/ProductTopSection/ProductTopSection';
import CrossSellingSection from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/PlateReferenceCard/CrossSellingSection/CrossSellingSection';
import { TireReferenceCardsContainer } from 'pages/TiresPage/TireReferencesSection/TireReferencesContainer/TireReferenceCardsContainer';
import { Box, CenterFlex, Flex, ImageWithLoader, InfoAlert, MarginBox, Panel, SectionCollapse, Text } from 'UI';
import { NOT_FOUND } from 'utils';
import { BorderedBox } from './Product.styled';
import { ProductDetails } from './ProductDetails';
import ReferenceCardLaborTimesWrapper from '../SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReferenceCardLaborTimesWrapper';

export interface ProductDetailProps {
  sparePartsView: SparePartsViewType;
  refLocal: DHReferenceLocal;
  query: string | undefined;
  isPopin?: boolean;
  seePlate?: React.ReactNode;
  scrollToSubstitutes?: boolean;
  isVehicleCatalog?: boolean;
  isUniversalProduct?: boolean;
  isAvailableSoon?: boolean;
  isNoLongerAvailable?: boolean;
  isCrossSellReference?: boolean;
  isDiscontinued?: boolean;
}

const shouldDisplayDetailSection = (refLocal: DHReferenceLocal) => {
  return (
    (refLocal.additionalInformation && refLocal.additionalInformation?.length > 0) ||
    refLocal.characteristics?.unitsOfSales ||
    (refLocal.characteristics?.technicalCharacteristics &&
      refLocal.characteristics?.technicalCharacteristics?.length > 0) ||
    (refLocal.descriptions?.familyDetails && refLocal.descriptions?.familyDetails?.length > 0) ||
    (refLocal.descriptions?.modelDetails && refLocal.descriptions?.modelDetails?.length > 0) ||
    (refLocal.descriptions?.partDetails && refLocal.descriptions?.partDetails?.length > 0) ||
    refLocal.includeFDSDocument
  );
};

const shouldDisplayApplicability = (refLocal: DHReferenceLocal) => {
  return refLocal.applicableVehicles && refLocal.applicableVehicles.length > 0;
};

export const ProductTab = ({
  sparePartsView,
  refLocal,
  query,
  isPopin,
  seePlate,
  isVehicleCatalog,
  scrollToSubstitutes = false,
  isUniversalProduct,
  isCrossSellReference,
  isDiscontinued,
}: ProductDetailProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const productMenuRef = useRef<HTMLDivElement>(null);
  const displayApplicability = shouldDisplayApplicability(refLocal);
  const displayDetailSection = shouldDisplayDetailSection(refLocal);
  const eanCode = refLocal.tireDetails?.eanCode;
  const isTire = refLocal.type === ('TIRE' as ReferenceType);
  const eprelUrl = refLocal.tireDetails?.eprelUrl;
  const availableInUserCountry = useSelector((state: RootState) =>
    getIsStockAvailable(state, refLocal.referenceNumber),
  );
  const replacementTires = useSelector((state: RootState) =>
    getTireSearchResult(state, {
      searchParamsBase64: refLocal.referenceNumber,
    }),
  );

  useEffect(() => {
    if (scrollToSubstitutes) {
      dispatch(setSelectedTab('substitute_references'));
      setTimeout(() => {
        productMenuRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useFetchLaborTimeIdsByReferenceGenericPart(refLocal.referenceNumber, vehicleKey);

  const laborTimeIdsWrapper = useSelector((state: RootState) =>
    getLaborTimeIdsForGenericPart(state, { vehicleKey, genericPart: refLocal.genericPart }),
  );

  useFetchMprValidation(refLocal.referenceNumber);

  const userCountry = useSelector(getUserCountry);
  const stock = useSelector((state: RootState) => getStockInfo(state, refLocal.referenceNumber));
  const hasStockInR1 = stock?.data?.warehouses.some((w) => w.type === 'LOCAL' && w.confirmedQuantity > 0) ?? false;
  const accepted =
    useSelector((state: RootState) => getIsMprAccepted(state, refLocal.referenceNumber) ?? true) ||
    (userCountry === 'FR' && hasStockInR1);

  const isAvailableSoon = isTire && refLocal.tireDetails?.lifeCycle === LIFECYCLE_W1;
  const isNoLongerAvailable =
    isTire &&
    !isAvailableSoon &&
    (refLocal.tireDetails?.isNonCommercialized ||
      (refLocal.tireDetails?.lifeCycle === LIFECYCLE_W3 && stock?.searchStatus === NOT_FOUND) ||
      refLocal.tireDetails?.lifeCycle === LIFECYCLE_W4 ||
      !accepted);

  useFetchTireReplacementProducts(refLocal.referenceNumber, false);

  const replacementTiresFound = replacementTires?.data?.tires;
  const Wrapper = isAvailableSoon || isNoLongerAvailable ? GrayedOut : React.Fragment;
  const imageFooter = eanCode && (
    <Flex justify={'flex-end'}>
      <MarginBox my={15}>
        <Text type="sm" color="clear_blue">
          {t('catalog.reference.ean_code', 'EAN CODE: {{eanCode}}', { eanCode })}
        </Text>
      </MarginBox>
    </Flex>
  );

  return (
    <>
      <CenterFlex data-cy={'product-tab'}>
        <Flex direction={'column'} maxWidth={'100%'}>
          <Wrapper>
            <ProductTopSection
              refLocal={refLocal}
              sparePartsView={sparePartsView}
              query={query}
              seePlate={seePlate}
              isVehicleCatalog={isVehicleCatalog}
              isUniversalProduct={isUniversalProduct}
              isAvailableSoon={isAvailableSoon}
              isNoLongerAvailable={isNoLongerAvailable}
              isPopin={isPopin}
              isCrossSellReference={isCrossSellReference}
              isDiscontinued={isDiscontinued}
              imageFooter={imageFooter}
            />
          </Wrapper>
          <Flex>
            {eprelUrl && (
              <Flex>
                <MarginBox my={15} mx={15}>
                  <ImageWithLoader
                    alt={'energy-label-image'}
                    imageUrl={eprelUrl}
                    fallbackComponent={
                      <ErrorWithLabel
                        label={t('catalog.vehicle.tires.eprel_error', 'Eprel image temporarily unavailable.')}
                      />
                    }
                    imageHeight={200}
                    imageWidth={130}
                  />
                </MarginBox>
              </Flex>
            )}
            {isNoLongerAvailable && availableInUserCountry && (
              <Flex align={'center'} justify={'flex-start'}>
                <InfoAlert
                  size={'lg'}
                  message={
                    <SFlex direction={'column'}>
                      <Text type="base" bold>
                        {t('catalog.tires.search.no_longer_available_header', 'Product no longer available')}
                      </Text>
                      <Text type={'search_result'}>
                        {t(
                          'catalog.tires.search.no_longer_available_text',
                          'We have listed our best equivalent products below',
                        )}
                      </Text>
                    </SFlex>
                  }
                />
              </Flex>
            )}
            {isAvailableSoon && availableInUserCountry && (
              <Flex align={'center'} justify={'flex-start'}>
                <InfoAlert
                  size={'lg'}
                  message={
                    <SFlex direction={'column'}>
                      <Text type="base" bold>
                        {t('catalog.tires.search.not_available_yet_header', 'Product not available yet')}
                      </Text>
                      <Text type={'search_result'}>
                        {t(
                          'catalog.tires.search.not_available_yet_text',
                          'We have listed our best replacement products below',
                        )}
                      </Text>
                    </SFlex>
                  }
                />
              </Flex>
            )}
          </Flex>
          <Box height={30} />
          <DataContainer
            data={refLocal.detailStatus}
            ErrorState={() => (
              <ErrorWithLabel
                label={t(
                  'common.view.product_replacing_references.backend_error',
                  'Equivalent products are temporarily unavailable, please try again later.',
                )}
              />
            )}
          >
            {(isNoLongerAvailable || isAvailableSoon) && (
              <Flex direction={isPopin ? 'column' : ['column', 'row']} justify={'center'}>
                <BorderedBox>
                  <MarginBox>
                    <DataContainer data={replacementTires?.searchStatus}>
                      {replacementTiresFound && (
                        <TireReferenceCardsContainer
                          title={t(
                            'catalog.search.iam.replacement_products_other',
                            'Replacement products ({{count}})',
                            {
                              count: replacementTiresFound.length,
                            },
                          )}
                          tires={[...replacementTiresFound]
                            .filter(
                              (replacementTire) =>
                                replacementTire.lifeCycle === LIFECYCLE_W2 ||
                                replacementTire.lifeCycle === LIFECYCLE_W3,
                            )
                            .sort((a, b) => a.brandName?.localeCompare(b.brandName ?? '') ?? 0)}
                          sparePartsView={sparePartsView}
                          singleSection={replacementTires && replacementTiresFound.length === 0}
                          initShowCount={5}
                          isAvailableSoon={isAvailableSoon}
                          isNoLongerAvailable={isNoLongerAvailable}
                          open
                          compact
                        />
                      )}
                    </DataContainer>
                  </MarginBox>
                </BorderedBox>
              </Flex>
            )}
          </DataContainer>
          <DataContainer
            data={refLocal.detailStatus}
            ErrorState={() => (
              <ErrorWithLabel
                label={t(
                  'common.view.product_details.backend_error',
                  'Product details are temporarily unavailable, please try again later.',
                )}
              />
            )}
          >
            <MarginBox mx={20} my={20} />
            <Flex direction={'column'} gap={15}>
              {displayDetailSection && (
                <SectionCollapse position={'end'} defaultActiveKey={''} noShadow>
                  <Panel header={t('common.description', 'Description')} key={'1'}>
                    <MarginBox mx={30} my={30}>
                      <ProductDetails reference={refLocal} />
                    </MarginBox>
                  </Panel>
                </SectionCollapse>
              )}
              {displayApplicability && (
                <SectionCollapse position={'end'} defaultActiveKey={''} noShadow>
                  <Panel header={t('common.applicability', 'Applicability')} key={'1'}>
                    <MarginBox mx={30} my={30}>
                      <Applicability referenceNumber={refLocal.referenceNumber} />
                    </MarginBox>
                  </Panel>
                </SectionCollapse>
              )}
            </Flex>
            <MarginBox my={20} />
            {isPopin && <MarginBox mt={30} />}
            <Flex size={0} minWidth={60} />
            <Flex ref={productMenuRef}>
              <ProductMenu refLocal={refLocal} sparePartsView={sparePartsView} />
            </Flex>
          </DataContainer>
        </Flex>
      </CenterFlex>
      <MarginBox mt={30} />
      {laborTimeIdsWrapper?.data && (
        <ReferenceCardLaborTimesWrapper
          referenceNumber={refLocal.referenceNumber}
          laborTimeIds={laborTimeIdsWrapper?.data}
          isProduct
        />
      )}
      <CrossSellingSection
        mainRef={refLocal.referenceNumber}
        crossSellingReferences={refLocal.crossSelling}
        type={'product'}
      />
    </>
  );
};
